import { useState, useEffect } from 'react';

interface GeoState {
  location: { lat: number; lng: number } | null;
  source: 'home' | 'gps' | 'fallback' | null;
  error: string | null;
  loading: boolean;
}

const VIENNA = { lat: 48.2082, lng: 16.3738 };
const SETTINGS_KEY = 'smartcart_settings';

function getSavedHome(): { lat: number; lng: number } | null {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s.home_lat != null && s.home_lng != null) {
        return { lat: s.home_lat, lng: s.home_lng };
      }
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Priority: 1) Saved home location  2) GPS  3) Vienna fallback
 */
export function useGeolocation(): GeoState {
  const [state, setState] = useState<GeoState>({ location: null, source: null, error: null, loading: true });

  useEffect(() => {
    // 1. Check saved home location
    const home = getSavedHome();
    if (home) {
      setState({ location: home, source: 'home', error: null, loading: false });
      return;
    }

    // 2. Try GPS
    if (!navigator.geolocation) {
      setState({ location: VIENNA, source: 'fallback', error: 'Geolokalisierung nicht unterstützt. Verwende Wien.', loading: false });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => setState({
        location: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        source: 'gps',
        error: null,
        loading: false,
      }),
      () => setState({
        location: VIENNA,
        source: 'fallback',
        error: 'Standortzugriff verweigert. Verwende Wien als Fallback.',
        loading: false,
      }),
    );
  }, []);

  return state;
}

/** Returns saved search radius from settings, or default 3000m. */
export function getSavedRadius(): number {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s.search_radius_m) return s.search_radius_m;
    }
  } catch { /* ignore */ }
  return 3000;
}

/** Returns saved products-per-concept from settings, or default 5. */
export function getSavedProductsPerConcept(): number {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s.products_per_concept) return s.products_per_concept;
    }
  } catch { /* ignore */ }
  return 5;
}
